import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import type { ApiPodcastModel } from "../../domains/podcast/hooks/useGetPodcastsListQuery";
import { useSharePodcast } from "../../domains/podcast/hooks/useSharePodcast";
import { getPodcastBackground } from "../../domains/room/utils/getPodcastBackground";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventSelectItemPodcast } from "../../tools/analytics/domains/podcast.analytics";
import { ContentPublicationCard } from "../ui/ContentPublicationCard";
import type { PublicationCardRecommendation } from "../ui/PublicationCard";
import { podcastSource } from "../ui/SourceLabel";

function formatDate(value: Date | string | null, locale: string): string {
  if (value === null) return "";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function resolveFirstEpisode(podcast: ApiPodcastModel) {
  const published = podcast.episodes.filter(
    (ep) => ep.meta.status === "published"
  );
  return published.at(0);
}

function resolveImageUrl(
  podcast: ApiPodcastModel,
  listIndex: number
): string | null {
  const episode = resolveFirstEpisode(podcast);
  if (episode !== undefined && episode.thumbnail !== null) {
    return episode.thumbnail.url;
  }
  return getPodcastBackground(listIndex);
}

export default function PodcastCard({
  roomId,
  podcast,
  listIndex,
  analyticsListName,
  recommendation,
  onClick,
  className,
}: {
  roomId: string;
  podcast: ApiPodcastModel;
  listIndex: number;
  analyticsListName: AnalyticsItemList;
  recommendation: PublicationCardRecommendation | null;
  onClick: (() => void) | null;
  className: string | null;
}) {
  const history = useHistory();
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const { triggerShare } = useSharePodcast();

  const firstEpisode = resolveFirstEpisode(podcast);
  const title = firstEpisode !== undefined ? firstEpisode.title : podcast.title;
  const imageUrl = resolveImageUrl(podcast, listIndex);
  const source = podcastSource(podcast.owner);
  const episodeCount = podcast.episodes.filter(
    (ep) => ep.meta.status === "published"
  ).length;
  const detail =
    episodeCount > 0 ? t("{{count}} episodes", { count: episodeCount }) : null;
  const date =
    firstEpisode !== undefined
      ? formatDate(firstEpisode.publication_date, activeLang)
      : "";

  return (
    <ContentPublicationCard
      contentType="podcast"
      title={title}
      source={source}
      date={date}
      meta={t("Podcast")}
      metaVariant="lemon"
      detail={detail}
      imageUrl={imageUrl}
      recommendation={recommendation}
      isBookmarked={false}
      onBookmark={null}
      onShare={() => {
        void triggerShare(podcast, roomId);
      }}
      onClick={() => {
        if (onClick !== null) onClick();
        gaEventSelectItemPodcast({
          podcast,
          listName: analyticsListName,
        });
        history.push(`/room/podcast/${roomId}/${podcast.id}`);
      }}
      className={className === null ? undefined : className}
    />
  );
}
